import React, { useMemo } from "react";
import { Box, Typography, Chip } from "@mui/material";
import TreeView from "@mui/lab/TreeView";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TreeItem from "@mui/lab/TreeItem";
import { useTranslation } from "react-i18next";

function findNodeById(nodes, nodeId) {
  if (!nodes) return null;
  for (const n of nodes) {
    if (n.id === nodeId) return n;
    const found = findNodeById(n.children, nodeId);
    if (found) return found;
  }
  return null;
}

/**
 * Read-only tree built from keyPath nodes. Shows missing badge when any leaf under has missing translations.
 */
const TranslationsTree = ({
  treeNodes,
  selectedKeyPath,
  selectedGroupKeyPath,
  onSelectLeaf,
  onSelectGroup,
  getMissingCount,
  filterApplied,
}) => {
  const { t } = useTranslation();

  const defaultExpanded = useMemo(() => {
    const ids = [];
    const collect = (nodes) => {
      (nodes || []).forEach((n) => {
        if (n.id) ids.push(n.id);
        if (n.children?.length) collect(n.children);
      });
    };
    collect(treeNodes);
    return ids;
  }, [treeNodes]);

  const handleNodeSelect = (event, nodeIds) => {
    const nodeId = Array.isArray(nodeIds) ? nodeIds[0] : nodeIds;
    if (!nodeId) return;
    const node = findNodeById(treeNodes, nodeId);
    if (node) {
      if (node.isLeaf) {
        onSelectLeaf(node.keyPath);
      } else {
        onSelectGroup(node.keyPath);
      }
    }
  };

  const renderNode = (node) => {
    const isSelected =
      (node.isLeaf && selectedKeyPath === node.keyPath) ||
      (!node.isLeaf && selectedGroupKeyPath === node.keyPath);
    const missingCount = node.isLeaf
      ? getMissingCount(node.keyPath)
      : null;
    const showMissing = missingCount != null && missingCount > 0;

    return (
      <TreeItem
        key={node.id}
        nodeId={node.id}
        label={
          <Box
            component="span"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              py: 0.25,
              bgcolor: isSelected ? "action.selected" : "transparent",
              borderRadius: 1,
              px: 0.5,
              cursor: "pointer",
            }}
          >
            <Typography variant="body2" noWrap sx={{ flex: 1 }} component="span">
              {node.label}
            </Typography>
            {showMissing && (
              <Chip
                size="small"
                label={missingCount}
                color="warning"
                sx={{ height: 18, fontSize: "0.7rem" }}
              />
            )}
          </Box>
        }
      >
        {Array.isArray(node.children) && node.children.length > 0
          ? node.children.map((child) => renderNode(child))
          : null}
      </TreeItem>
    );
  };

  const isEmpty = !treeNodes || treeNodes.length === 0;

  return isEmpty ? (
    <Box sx={{ p: 2 }}>
      <Typography color="text.secondary" variant="body2">
        {filterApplied
          ? t("dsokCustomerDetail.translationsUI.noResults")
          : t("dsokCustomerDetail.translationsUI.emptyTree")}
      </Typography>
    </Box>
  ) : (
    <TreeView
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpandIcon={<ChevronRightIcon />}
      defaultExpanded={defaultExpanded}
      selected={
        selectedKeyPath
          ? [selectedKeyPath]
          : selectedGroupKeyPath
            ? [`group:${selectedGroupKeyPath}`]
            : []
      }
      onNodeSelect={handleNodeSelect}
      sx={{ flex: 1, overflow: "auto", py: 1 }}
    >
      {treeNodes.map((node) => renderNode(node))}
    </TreeView>
  );
};

export default TranslationsTree;
